import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor - attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('sambal_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sambal_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// API methods
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/update-profile', data),
  updatePassword: (data) => api.put('/auth/update-password', data),
};

export const userAPI = {
  getDashboard: () => api.get('/users/dashboard'),
  getApplications: () => api.get('/users/applications'),
  lookupByAadhaar: (aadhaar) => api.get(`/users/lookup-by-aadhaar/${aadhaar}`),
  submitDeathApp: (data) => api.post('/users/applications/death', data),
  submitKanyaApp: (data) => api.post('/users/applications/kanya', data),
  getSahyog: () => api.get('/users/sahyog'),
  submitSahyog: (data) => api.post('/users/sahyog', data),
  getReferralStats: () => api.get('/users/referral-stats'),
  submitRedeemRequest: (data) => api.post('/users/redeem-request', data),
  getMyRedeemRequests: () => api.get('/users/redeem-requests'),
};

export const adminAPI = {
  getDashboard: () => api.get('/admin/dashboard'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id, data) => api.put(`/admin/users/${id}/reset-password`, data),
  getApplications: (params) => api.get('/admin/applications', { params }),
  getAnalytics: (period) => api.get('/admin/analytics', { params: { period } }),
  updateApplication: (id, data) => api.put(`/admin/applications/${id}`, data),
  getSahyog: (params) => api.get('/admin/sahyog', { params }),
  verifySahyog: (id, data) => api.put(`/admin/sahyog/${id}/verify`, data),
};

export const superAdminAPI = {
  getDashboard: () => api.get('/super-admin/dashboard'),
  getAdmins: () => api.get('/super-admin/admins'),
  createAdmin: (data) => api.post('/super-admin/admins', data),
  updateAdmin: (id, data) => api.put(`/super-admin/admins/${id}`, data),
  deleteAdmin: (id) => api.delete(`/super-admin/admins/${id}`),
  getUsers: (params) => api.get('/super-admin/users', { params }),
  getUser: (id) => api.get(`/super-admin/users/${id}`),
  updateUser: (id, data) => api.put(`/super-admin/users/${id}`, data),
  approveUser: (id) => api.put(`/super-admin/users/${id}/approve`),
  blockUser: (id) => api.put(`/super-admin/users/${id}/block`),
  toggleUserActive: (id) => api.put(`/super-admin/users/${id}/toggle-active`),
  deleteUser: (id) => api.delete(`/super-admin/users/${id}`),
  resetUserPassword: (id, data) => api.put(`/super-admin/users/${id}/reset-password`, data),
  resetPassword: (id, data) => api.put(`/super-admin/admins/${id}/reset-password`, data),
  getSystemStats: () => api.get('/super-admin/system-stats'),
  // Redeem
  getRedeemRequests: (params) => api.get('/super-admin/redeem-requests', { params }),
  approveRedeem: (id, data) => api.put(`/super-admin/redeem-requests/${id}/approve`, data),
  rejectRedeem: (id, data) => api.put(`/super-admin/redeem-requests/${id}/reject`, data),
  adminRedeemUser: (id, data) => api.put(`/super-admin/users/${id}/admin-redeem`, data),
  // Age groups
  getAgeGroups: (params) => api.get('/super-admin/age-groups', { params }),
  getAgeGroupMembers: (key, params) => api.get(`/super-admin/age-groups/${key}/members`, { params }),
  // Gallery
  getGallery: () => api.get('/super-admin/gallery'),
  addGalleryImage: (data) => api.post('/super-admin/gallery', data),
  updateGalleryImage: (id, data) => api.put(`/super-admin/gallery/${id}`, data),
  deleteGalleryImage: (id) => api.delete(`/super-admin/gallery/${id}`),
  // Home page campaign slider
  getCampaigns: () => api.get('/super-admin/campaigns'),
  addCampaign: (data) => api.post('/super-admin/campaigns', data),
  updateCampaign: (id, data) => api.put(`/super-admin/campaigns/${id}`, data),
  deleteCampaign: (id) => api.delete(`/super-admin/campaigns/${id}`),
};

export const publicAPI = {
  getMembers: (params) => api.get('/members', { params }),
  getMemberCount: () => api.get('/members/count'),
  getPublicApplications: (params) => api.get('/applications/public', { params }),
  getGallery: (params) => api.get('/gallery', { params }),
  getActiveCampaigns: () => api.get('/campaigns/active'),
};

// kind: 'gallery' | 'campaigns'. `version` (updatedAt) busts the browser cache after an edit.
export const mediaUrl = (kind, id, version) =>
  `${api.defaults.baseURL}/${kind}/${id}/image${version ? `?v=${new Date(version).getTime()}` : ''}`;

export default api;